import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { hot } from 'react-hot-loader';
import * as queryString from 'query-string';

import AppRouter from './AppRouter';
import './App.scss';

import { AccountData, ApplicationState, Authentication } from '../../models';
import { DEFAULT_TOKEN_NAME } from '../../constants';
import { setToken, getToken, getUserDataFromToken } from '../../helpers/tokens';
import * as AccountActions from '../../actions/account/account';
import * as AuthenticationActions from '../../actions/authentication/authentication';

import { getTenantSubdomainFromUrl } from '../../helpers/urls';
import { handleTenantIdParam } from '../../helpers/tenantId';
import { ErrorService } from './ErrorService';

export interface AppProps {
  setAccountData: (accountData: AccountData | object) => AccountData | object;
  getSSOToken: () => void;
  authentication: Authentication;
  account: AccountData;
}

const mapStateToProps = ({ authentication, account }: ApplicationState) => {
  return {
    authentication,
    account,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => {
  return {
    setAccountData: (accountData: AccountData | object) =>
      dispatch(AccountActions.setAccountData(accountData)),
    getSSOToken: () => dispatch(AuthenticationActions.requestSSOToken()),
  };
};

class App extends React.Component<AppProps | null> {
  completeOAUTHLogin(accessToken: string) {
    setToken(DEFAULT_TOKEN_NAME, accessToken);
    localStorage.setItem('authMethod', 'original');
    const previousHref =
      window.sessionStorage.getItem('oauth.previous-href') || '/';
    window.location.replace(previousHref);
  }

  startOAUTHLogin(ssoUrl: string) {
    // never save /logout here or we'll create an infinite loop!
    const returnToUrl =
      window.location.pathname !== '/logout'
        ? window.location.href
        : window.location.origin;
    window.sessionStorage.setItem('oauth.previous-href', returnToUrl);
    window.location.replace(`${ssoUrl}`);
  }

  isAsureIdRoute() {
    const path = window.location.pathname;
    return (
      path.includes('/asure-id') ||
      path.includes('/callback') ||
      path.startsWith('/asure-id/')
    );
  }

  async startApp() {
    // If the current route is an AsureId route (but not callback), skip the regular authentication flow
    if (this.isAsureIdRoute() && window.location.pathname !== '/callback') {
      // For AsureId routes like /asure-id, we'll just render the app with a placeholder account
      // This allows the AsureIdPage to load and handle its own auth flow
      if (!this.props.account.loadedFromToken) {
        const placeholderAccount = { loadedFromToken: true };
        this.props.setAccountData(placeholderAccount);
      }
      return;
    }
    // For backward compatibility, support ?tenantId or ?_tenantId in place of subdomain.
    // DEPRECATED: remove when no longer in use by legacy links.
    if (await handleTenantIdParam()) {
      return;
    }

    // tenant subdomain must be present, or we show error page
    if (!getTenantSubdomainFromUrl()) {
      new ErrorService().showNotFound();
      return;
    }

    const accessToken = getToken();

    // Handle authentication based on method
    if (accessToken) {
      const authMethod = localStorage.getItem('authMethod');
      if (authMethod === 'asure-id') {
        // For Asure ID, load account data from localStorage
        const storedAccountData = localStorage.getItem('accountData');
        if (storedAccountData) {
          const accountData = JSON.parse(storedAccountData);
          this.props.setAccountData(accountData);
        } else {
          // Fallback if no stored data
          this.props.getSSOToken();
        }
      } else {
        // For original auth, load from token
        const { setAccountData } = this.props;
        const accountData = getUserDataFromToken(accessToken) as AccountData;
        setAccountData(accountData);
      }
    } else {
      // trigger getting the sso token
      //  which will cause a redux state change to include an sso token
      //  which will then cause a redirect to heisenberg (see componentWillReceiveProps)
      this.props.getSSOToken();
    }
  }

  async UNSAFE_componentWillMount() {
    const accessToken = queryString.parse(window.location.search).access_token;
    if (accessToken) {
      this.completeOAUTHLogin(accessToken);
    } else {
      await this.startApp();
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: AppProps) {
    // Skip SSO redirect for AsureId routes
    if (this.isAsureIdRoute()) {
      return;
    }

    // Redirect to SSO to authenticate after token request
    const { ssoToken, ssoUrl } = nextProps.authentication;
    if (ssoToken && ssoUrl) {
      this.startOAUTHLogin(ssoUrl);
    }
  }

  render() {
    if (
      this.props.account.loadedFromToken ||
      window.location.pathname === '/callback' ||
      window.location.pathname === '/asure-id'
    ) {
      return <AppRouter account={this.props.account} />;
    } else {
      return null;
    }
  }
}

const ConnectedApp = connect(mapStateToProps, mapDispatchToProps)(App);
export default hot(module)(ConnectedApp);
